import { Link, useLocation } from 'react-router-dom';
import { TrendingUp, Mail, Zap, BarChart3, Send, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const handleSubscribe = () => {
    // Navigate to ORB Algo pricing section
    if (location.pathname === '/orb-algo') {
      // Already on ORB page, just scroll to pricing
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to ORB page with pricing hash
      window.location.href = '/orb-algo#pricing';
    }
  };

  return (
    <footer className="bg-[#030305] border-t border-white/5" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-3">
              <img 
                src="/logo.png" 
                alt="Monk Algo" 
                className="w-8 h-8 rounded-lg object-cover"
              />
              <span className="font-heading font-semibold text-base text-white">Monk Algo</span>
            </Link>
            <p className="font-body text-white/40 text-xs leading-relaxed">
              Algorithmic trading systems for MT5 and NinjaTrader 8, with backtests you can run yourself.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-3 text-sm">Product</h4>
            <ul className="space-y-2">
              {/* Monk Breakout EA - HIDDEN (not deleted, can be re-enabled later) */}
              {/* <li>
                <Link to="/ea" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors" data-testid="footer-link-ea">
                  <TrendingUp size={11} /> Monk Breakout EA
                </Link>
              </li> */}
              {/* Inside Bar Algo - HIDDEN (not deleted, can be re-enabled later) */}
              {/* <li>
                <Link to="/inside-bar" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors" data-testid="footer-link-inside-bar">
                  <BarChart3 size={11} /> Inside Bar Algo
                </Link>
              </li>
              <li>
                <a href="/inside-bar#performance" className="font-body text-white/40 hover:text-white text-xs transition-colors">
                  2025 Performance
                </a>
              </li>
              <li>
                <a href="/inside-bar#pricing" className="font-body text-white/40 hover:text-white text-xs transition-colors">
                  Pricing
                </a>
              </li> */}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-3 text-sm">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/terms-of-service" className="font-body text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-link-terms">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="font-body text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="font-body text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-link-refund">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/risk-disclosure" className="font-body text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-link-risk">
                  Risk Disclosure
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-3 text-sm">Connect</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors" data-testid="footer-link-contact">
                  <Mail size={11} /> Contact Us
                </Link>
              </li>
              <li>
                <a href="mailto:support@monkalgo.com" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors" data-testid="footer-email">
                  <Mail size={11} /> support@monkalgo.com
                </a>
              </li>
              <li>
                <a href="https://t.me/+9yeoU7R5h3k2NDVl" target="_blank" rel="noopener noreferrer" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors">
                  <Send size={11} /> Telegram Results
                </a>
              </li>
              <li>
                <a href="https://instagram.com/monkalgo" target="_blank" rel="noopener noreferrer" className="font-body text-white/40 hover:text-white text-xs flex items-center gap-1.5 transition-colors">
                  <Instagram size={11} /> Instagram
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Bar */}
        <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-[#8B5CF6]/10 to-[#10B981]/10 border border-[#8B5CF6]/20 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-heading font-semibold text-white text-sm">Start Automated Trading Today</p>
            <p className="font-body text-white/50 text-xs">Explore Monk Algo ORB for NAS100</p>
          </div>
          <button
            onClick={handleSubscribe}
            className="btn-purple text-sm flex items-center gap-2"
            data-testid="footer-cta-btn"
          >
            <Zap size={14} />
            View Plans
          </button>
        </div>

        <div className="mt-8 pt-5 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="font-body text-white/30 text-xs">© {currentYear} Monk Algo. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-3 text-xs">
            <Link to="/privacy-policy" className="font-body text-white/30 hover:text-white/50 transition-colors">Privacy</Link>
            <span className="text-white/10">|</span>
            <Link to="/terms-of-service" className="font-body text-white/30 hover:text-white/50 transition-colors">Terms</Link>
            <span className="text-white/10">|</span>
            <Link to="/refund-policy" className="font-body text-white/30 hover:text-white/50 transition-colors">Refunds</Link>
            <span className="text-white/10">|</span>
            <Link to="/risk-disclosure" className="font-body text-white/30 hover:text-white/50 transition-colors">Risk</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
